import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ChevronLeft, Sparkles, Star, ExternalLink } from "lucide-react";
import client, { fmtUsd, fmtPct, fmtNum } from "../lib/api";
import { LineChart, Line, ResponsiveContainer, Tooltip, XAxis, YAxis, Area, AreaChart } from "recharts";
import ReactMarkdown from "react-markdown";
import { useAuth } from "../context/AuthContext";

const AssetDetail = () => {
  const { type, id } = useParams();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [chart, setChart] = useState([]);
  const [news, setNews] = useState([]);
  const [insight, setInsight] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [range, setRange] = useState(30);
  const [watched, setWatched] = useState(false);

  useEffect(() => {
    setData(null); setChart([]); setNews([]); setInsight(null);
    if (type === "crypto") {
      client.get(`/crypto/coin/${id}`).then(({ data }) => setData(data));
      client.get(`/crypto/chart/${id}?days=${range}`).then(({ data }) => setChart(data.prices || []));
      client.get(`/news?q=${encodeURIComponent(id)}`).then(({ data }) => setNews(data.articles || []));
    } else {
      client.get(`/stocks/quote/${id}`).then(({ data }) => setData(data));
      client.get(`/stocks/history/${id}`).then(({ data }) => {
        const candles = (data.candles || []).map((d) => ({ t: new Date(d.date).getTime(), price: d.close }));
        setChart(candles);
      });
      client.get(`/news?q=${encodeURIComponent(id)}`).then(({ data }) => setNews(data.articles || []));
    }
  }, [type, id, range]);

  useEffect(() => {
    if (!user) return;
    client.get("/watchlist").then(({ data }) => {
      const isIn = (data.items || []).some((w) => w.symbol === id && w.asset_type === type);
      setWatched(isIn);
    }).catch(() => {});
  }, [user, id, type]);

  const generateInsight = async () => {
    setAiLoading(true);
    try {
      const payload = {
        asset_symbol: type === "crypto" ? data?.symbol?.toUpperCase() || id : id,
        asset_type: type,
        asset_name: data?.name || id,
        price: type === "crypto" ? data?.price : data?.price,
        change_24h: type === "crypto" ? data?.price_change_24h : parseFloat((data?.change_percent || "0").replace("%", "")),
        news: news.slice(0, 6),
      };
      const { data: res } = await client.post("/ai/insights", payload);
      setInsight(res.analysis);
    } catch (e) {
      setInsight("Failed to generate insight: " + (e.response?.data?.detail || e.message));
    } finally { setAiLoading(false); }
  };

  const toggleWatch = async () => {
    if (!user) { alert("Sign in to save to your watchlist."); return; }
    try {
      if (watched) {
        await client.delete(`/watchlist/${type}/${id}`);
        setWatched(false);
      } else {
        await client.post("/watchlist", {
          symbol: id,
          asset_type: type,
          name: data?.name || id,
        });
        setWatched(true);
      }
    } catch (e) { console.error(e); }
  };

  if (!data) return <div className="text-neutral-500 p-12 text-center" data-testid="asset-loading">Loading asset…</div>;

  const changePct = type === "crypto" ? data.price_change_24h : parseFloat((data.change_percent || "0").replace("%", ""));
  const price = data.price;
  const chartData = chart.map((p) => ({ time: p.t, price: p.price }));

  return (
    <div className="space-y-8 fade-in-up" data-testid="asset-detail">
      <Link to="/dashboard" className="inline-flex items-center gap-2 text-sm text-neutral-400 hover:text-white">
        <ChevronLeft size={14} /> Back
      </Link>

      {/* Header */}
      <div className="glass p-6 lg:p-8">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div className="flex items-center gap-4">
            {type === "crypto" && data.image && <img src={data.image} alt={data.name} className="w-14 h-14 rounded-full" />}
            {type === "stock" && (
              <div className="w-14 h-14 rounded-xl bg-white/10 border border-white/20 flex items-center justify-center font-display text-lg">
                {(id || "").slice(0, 2)}
              </div>
            )}
            <div>
              <div className="flex items-center gap-3">
                <h1 className="font-display text-3xl tracking-tight">{data.name || data.symbol || id}</h1>
                <span className="chip">{type === "crypto" ? "CRYPTO" : "STOCK"}</span>
              </div>
              <div className="text-neutral-500 text-sm mt-1 uppercase">{data.symbol || id}</div>
            </div>
          </div>
          <div className="flex items-end gap-6">
            <div>
              <div className="text-xs uppercase tracking-widest text-neutral-500">Price</div>
              <div className="font-display text-4xl font-mono-num mt-1">{fmtUsd(price)}</div>
              <div className={`text-sm font-mono-num mt-1 ${changePct >= 0 ? "up" : "down"}`}>
                {fmtPct(changePct)} · 24h
              </div>
            </div>
            <button data-testid="watchlist-toggle" onClick={toggleWatch} className={`btn-glass !py-2 !px-3 ${watched ? "!border-yellow-400/40" : ""}`}>
              <Star size={16} fill={watched ? "#fbbf24" : "none"} color={watched ? "#fbbf24" : "currentColor"} />
            </button>
          </div>
        </div>
      </div>

      {/* Chart */}
      <div className="glass p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="text-xs uppercase tracking-widest text-neutral-500">Price Chart</div>
          {type === "crypto" && (
            <div className="flex gap-1">
              {[7, 30, 90, 365].map((d) => (
                <button
                  key={d}
                  data-testid={`range-${d}`}
                  onClick={() => setRange(d)}
                  className={`px-3 py-1 rounded-lg text-xs font-mono-num transition-colors ${range === d ? "bg-white text-black" : "text-neutral-400 hover:text-white"}`}
                >
                  {d === 365 ? "1Y" : `${d}D`}
                </button>
              ))}
            </div>
          )}
        </div>
        <div style={{ width: "100%", height: 340 }}>
          <ResponsiveContainer>
            <AreaChart data={chartData} margin={{ top: 8, right: 8, left: 8, bottom: 8 }}>
              <defs>
                <linearGradient id="grad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={changePct >= 0 ? "#10b981" : "#ef4444"} stopOpacity={0.4} />
                  <stop offset="100%" stopColor={changePct >= 0 ? "#10b981" : "#ef4444"} stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis dataKey="time" tickFormatter={(t) => new Date(t).toLocaleDateString(undefined, { month: "short", day: "numeric" })} stroke="#525252" tick={{ fontSize: 11 }} />
              <YAxis domain={["auto", "auto"]} tickFormatter={(v) => fmtUsd(v, { compact: true, digits: 0 })} stroke="#525252" tick={{ fontSize: 11 }} width={60} />
              <Tooltip
                contentStyle={{ background: "rgba(10,10,10,0.9)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, fontSize: 12 }}
                labelFormatter={(t) => new Date(t).toLocaleString()}
                formatter={(v) => [fmtUsd(v), "Price"]}
              />
              <Area type="monotone" dataKey="price" stroke={changePct >= 0 ? "#10b981" : "#ef4444"} strokeWidth={2} fill="url(#grad)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Stats */}
        <div className="glass p-6 lg:col-span-4">
          <div className="text-xs uppercase tracking-widest text-neutral-500 mb-4">Key Stats</div>
          <dl className="space-y-3 text-sm">
            {type === "crypto" ? (
              <>
                <Row label="Market Cap" value={fmtUsd(data.market_cap, { compact: true, digits: 2 })} />
                <Row label="Volume 24h" value={fmtUsd(data.volume_24h, { compact: true, digits: 2 })} />
                <Row label="High 24h" value={fmtUsd(data.high_24h)} />
                <Row label="Low 24h" value={fmtUsd(data.low_24h)} />
                <Row label="ATH" value={fmtUsd(data.ath)} />
                <Row label="ATL" value={fmtUsd(data.atl)} />
                <Row label="7d" value={fmtPct(data.price_change_7d)} cls={data.price_change_7d >= 0 ? "up" : "down"} />
                <Row label="Supply" value={fmtNum(data.circulating_supply, { compact: true })} />
              </>
            ) : (
              <>
                <Row label="Open" value={fmtUsd(data.open)} />
                <Row label="High" value={fmtUsd(data.high)} />
                <Row label="Low" value={fmtUsd(data.low)} />
                <Row label="Previous Close" value={fmtUsd(data.previous_close)} />
                <Row label="Volume" value={fmtNum(data.volume, { compact: true })} />
                <Row label="Last Trade" value={data.latest_trading_day} />
              </>
            )}
          </dl>
        </div>

        {/* AI Insight */}
        <div className="glass p-6 lg:col-span-8" data-testid="ai-insights">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Sparkles size={14} className="text-blue-400" />
              <div className="text-xs uppercase tracking-widest text-neutral-500">Claude AI Insights</div>
            </div>
            <button data-testid="generate-insight" onClick={generateInsight} disabled={aiLoading} className="btn-accent !py-2 !px-4 text-sm">
              {aiLoading ? "Analyzing…" : insight ? "Regenerate" : "Generate Insight"}
            </button>
          </div>
          {!insight && !aiLoading && (
            <p className="text-neutral-500 text-sm">Click Generate to analyse the asset using live news + price action. Claude Sonnet 4.5 will produce a balanced bull/bear take with a suggested action.</p>
          )}
          {insight && (
            <div className="text-sm text-neutral-200 leading-relaxed asset-ai">
              <ReactMarkdown
                components={{
                  h1: ({ node, ...p }) => <h3 className="font-display text-lg mt-4 mb-2 text-white" {...p} />,
                  h2: ({ node, ...p }) => <h3 className="font-display text-base uppercase tracking-widest text-neutral-400 mt-5 mb-2" {...p} />,
                  h3: ({ node, ...p }) => <h4 className="font-display text-base mt-4 mb-1 text-white" {...p} />,
                  p: ({ node, ...p }) => <p className="mb-3 text-neutral-200" {...p} />,
                  ul: ({ node, ...p }) => <ul className="space-y-1 mb-3 list-disc pl-5 text-neutral-200" {...p} />,
                  li: ({ node, ...p }) => <li className="leading-relaxed" {...p} />,
                  strong: ({ node, ...p }) => <strong className="text-white font-medium" {...p} />,
                  em: ({ node, ...p }) => <em className="text-neutral-500 not-italic block mt-4 text-xs" {...p} />,
                }}
              >
                {insight}
              </ReactMarkdown>
            </div>
          )}
        </div>
      </div>

      {/* News */}
      <div>
        <div className="text-xs uppercase tracking-widest text-neutral-500 mb-4">Related News</div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {news.slice(0, 9).map((a) => (
            <a key={a.url} href={a.url} target="_blank" rel="noreferrer" data-testid={`asset-news-${a.url}`} className="glass glass-hover p-5 block">
              <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-2">{a.source}</div>
              <div className="font-display text-base leading-snug line-clamp-3">{a.title}</div>
              {a.description && <p className="text-xs text-neutral-400 mt-2 line-clamp-2">{a.description}</p>}
              <div className="text-[10px] text-neutral-500 mt-3 flex items-center gap-1"><ExternalLink size={10} /> {a.published_at && new Date(a.published_at).toLocaleDateString()}</div>
            </a>
          ))}
          {news.length === 0 && <div className="text-neutral-500 text-sm col-span-3">No recent news.</div>}
        </div>
      </div>
    </div>
  );
};

const Row = ({ label, value, cls }) => (
  <div className="flex items-center justify-between border-b border-white/5 pb-2">
    <dt className="text-neutral-500">{label}</dt>
    <dd className={`font-mono-num ${cls || ""}`}>{value ?? "—"}</dd>
  </div>
);

export default AssetDetail;
